<template>
  <div :class="['product-filter-bar', { 'product-filter-bar--has-fractions': hasFractions && showFractionFilter }]">
    <div
      v-if="showSortOrder"
      class="product-filter-bar__amount">
      <template v-if="currentCount && currentCount < totalCount">
        {{ currentCount }} / {{ totalCount }}
      </template>
      <template v-else>
        {{ totalCount }}
      </template>
      {{ tStatic('filterBarTitleCount') }}
    </div>

    <div class="product-filter-bar__actions">
      <div v-if="enableSearch" class="product-filter-bar__input-container">
        <oie-base-input
          v-model="proxySearchQuery"
          class="product-filter-bar__input"
          :placeholder="tStatic('searchInList')">
        </oie-base-input>
        <button v-if="proxySearchQuery" class="instant-search__clear" @click="proxySearchQuery = ''">
          <oie-use-svg svg="close"></oie-use-svg>
        </button>
        <div class="product-filter-bar__search-icon">
          <oie-use-svg svg="search"></oie-use-svg>
        </div>
      </div>
      <oie-base-action
        v-if="useListViewComputed && showSortOrder"
        theme="skeleton"
        @click="handleToggleShowAllVariants()">
        <template #iconPrepend>
          <oie-base-icon size="s" :icon="showAllVariants ? 'checkbox-checked-filled' : 'checkbox'"></oie-base-icon>
        </template>
        {{ tStatic('filterBarTitleShowArticle') }}
      </oie-base-action>

      <oie-base-action
        v-if="useListViewComputed"
        theme="skeleton"
        @click="toggleValue({ key: 'showTechnicalnformation' })">
        <template #iconPrepend>
          <oie-base-icon size="s" :icon="showTechnicalnformation ? 'checkbox-checked-filled' : 'checkbox'"></oie-base-icon>
        </template>
        {{ tStatic('filterBarTitleShowTechInfo') }}
      </oie-base-action>

      <oie-base-action
        v-if="showFacetFilterToggle"
        theme="skeleton"
        @click="handleFilterToggle">
        <template #iconPrepend>
          <oie-base-icon size="s" icon="settings-adjust"></oie-base-icon>
        </template>
        {{ tStatic('toggleFilters') }}
      </oie-base-action>

      <oie-base-dropdown
        v-if="showSortOrder"
        :initial-text="tStatic('filterBarDropDownTitleSortorder')"
        css-classes="product-filter-bar__dropdown"
        :options="sortOrderList"
        name="sort-products"
        :is-large="false">
      </oie-base-dropdown>

      <div v-if="mq('medium')" class="product-filter-bar__view-actions">
        <oie-base-action :theme="useListViewComputed ? 'outlined' : 'skeleton'" @click="switchViewType()">
          <oie-base-icon icon="list" size="s"></oie-base-icon>
        </oie-base-action>

        <oie-base-action :theme="!useListViewComputed ? 'outlined' : 'skeleton'" @click="switchViewType()">
          <oie-base-icon icon="grid" size="s"></oie-base-icon>
        </oie-base-action>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import _ from 'lodash'

export default {
  props: {
    totalCount: {
      type: Number,
      default: 0
    },
    currentCount: {
      type: Number,
      default: 0
    },
    useCompare: {
      type: Boolean,
      default: true
    },
    showSortOrder: {
      type: Boolean,
      default: true
    },
    showFractionFilter: {
      type: Boolean,
      default: false
    },
    searchQuery: {
      type: String,
      default: ''
    },
    enableSearch: {
      type: Boolean,
      default: false
    },
    showFacetFilterToggle: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    checkboxValue: false,
    sortOrderList: null,
    proxySearchQuery: ''
  }),
  computed: {
    ...mapGetters('ui', ['useListView', 'forceListView', 'forceGridView', 'showTechnicalnformation', 'showCompareProducts', 'showDesktopFilter']),
    ...mapGetters('fractions', ['allFractions']),
    ...mapGetters('productVariants', ['showAllVariants']),
    hasFractions() {
      return this.allFractions !== undefined && this.allFractions.length > 0
    },
    useListViewComputed() {
      if (this.forceListView) { return true }
      if (this.forceGridView) { return false }
      return this.useListView
    }
  },
  watch: {
    proxySearchQuery: _.debounce(function (newVal, oldVal) {
      if(newVal !== oldVal) {
        this.$emit('update:searchQuery', this.proxySearchQuery)
      }
    }, 200),
    searchQuery(newVal) {
      if(this.proxySearchQuery !== newVal) {
        this.proxySearchQuery = newVal
      }
    }
  },
  mounted() {
    _.forEach(['resize', 'orientationchange'], event => {
      window.addEventListener(event, _.throttle(() => {
        this.changeListView()
      }, 100))
    })

    this.sortOrderList = this.clientModel.sortOrderProductList
    this.changeListView()
  },
  beforeDestroy() {
    _.forEach(['resize', 'orientationchange'], event => {
      window.removeEventListener(event, _.throttle(() => {
        this.changeListView()
      }, 100))
    })
  },
  methods: {
    ...mapActions('ui', ['toggleValue', 'setActiveSlideIn']),
    ...mapActions('productVariants', ['toggleShowAllVariants']),
    handleToggleShowAllVariants() {
      this.toggleShowAllVariants()
      this.publish('toggle.articles.all', this.showAllVariants)
    },
    changeListView() {
      if (this.mq('to-medium')) {
        this.toggleValue({ key: 'forceView', data: null })
        this.toggleValue({ key: 'useListView', data: false })
      }
    },
    switchViewType() {
      this.toggleValue({ key: 'useListView', data: !this.useListViewComputed })
      this.toggleValue({ key: 'forceView', data: null })
    },
    handleFilterToggle() {
      if (this.mq('small')) {
        this.toggleValue({ key: 'showDesktopFilter' })
      } else {
        this.setActiveSlideIn('filters')
      }
    }
  }
}
</script>

<style lang="scss">
.product-filter-bar {
  @include container;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: color(background-white);
  padding: measure(quarter);
  border: 1px solid color(border-gray);

  @include media(medium) {
    min-height: 4.375rem;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    flex-wrap: nowrap;
    padding: measure(quarter) measure(half);
  }
}

.product-filter-bar--has-fractions {
  border-bottom: none;
}

.product-filter-bar__amount {
  @include h3-semi-bold;
  flex-shrink: 0;
  margin-right: 0;
  margin-bottom: measure(quarter);

  @include media(medium) {
    margin-right: auto;
    margin-bottom: 0;
  }
}

.product-filter-bar__actions {
  display: flex;
  flex-direction: column;
  gap: measure(quarter);
  align-items: flex-start;

  @include media(medium) {
    flex-grow: 1;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    width: auto;
    margin-left: measure(quarter);
  }
}

.product-filter-bar__filter-button {
  order: 1;
  padding: measure(quarter);
  padding-left: 0px;

  @include media(medium) {
    order: 0;
    padding: 0px
  }
}

.product-filter-bar__input-container {
  width: auto;
  flex-grow: 1;
  max-width: 30rem;
  display: flex;
  border: 1px solid color(border-gray)
}

.product-filter-bar__input .input.input__element{
  flex-grow: 1;
  border: none;
}

.product-filter-bar__clear-search {
  @include reset-button;
}

.product-filter-bar__search-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: measure(quarter);

  > svg {
    height: 1.5rem;
    width: 1.5rem;
  }
}

.product-filter-bar__view-actions  {
  display: flex;
  align-items: center;
  justify-content: left;
  flex-direction: row;
}

.product-filter-bar__view-button {
  width: 1.25rem;
  height: 1.25rem;
  color: color(disabled-item);
  background-color: transparent;

  &:first-child {
    margin-right: measure(quarter);
  }
}

.product-filter-bar__view-button svg {
  width: 100%;
  height: 100%;
}

.product-filter-bar__dropdown {
  min-width: 11.25rem;
}

.product-filter-bar__active-item {
  pointer-events: none;

  & > svg {
    color: color(enabled-item);
  }
}
</style>
